/**
 * Map utility functions
 * Shared helpers for map operations
 */

/**
 * Extract Strava activity ID from URL
 * @param {string} stravaUrl - Full Strava activity URL
 * @returns {string|null} Activity ID or null
 */
export const extractActivityId = (stravaUrl) => {
  if (!stravaUrl) return null;
  const match = stravaUrl.match(/activities\/(\d+)/);
  return match ? match[1] : null;
};

/**
 * Calculate bounds for a set of routes
 * @param {Object} routes - Object with activityId as key and coordinates array as value
 * @returns {google.maps.LatLngBounds|null} Bounds object or null if no valid coordinates
 */
export const calculateBounds = (routes) => {
  if (!routes || Object.keys(routes).length === 0) {
    return null;
  }

  if (!window.google?.maps?.LatLngBounds) {
    console.warn('Google Maps not loaded');
    return null;
  }

  const bounds = new window.google.maps.LatLngBounds();
  let hasValidCoords = false;

  Object.values(routes).forEach(coordinates => {
    if (coordinates && Array.isArray(coordinates)) {
      coordinates.forEach(coord => {
        if (coord && typeof coord.lat === 'number' && typeof coord.lng === 'number') {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
          hasValidCoords = true;
        }
      });
    }
  });

  return hasValidCoords ? bounds : null;
};

/**
 * Calculate bounds for a single route's coordinates
 * @param {Array} coordinates - Array of {lat, lng} objects
 * @returns {google.maps.LatLngBounds|null} Bounds object or null if no valid coordinates
 */
export const calculateRouteBounds = (coordinates) => {
  if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) {
    return null;
  }

  if (!window.google?.maps?.LatLngBounds) {
    console.warn('Google Maps not loaded');
    return null;
  }

  const bounds = new window.google.maps.LatLngBounds();
  let hasValidCoords = false;

  coordinates.forEach(coord => {
    if (coord && typeof coord.lat === 'number' && typeof coord.lng === 'number') {
      bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
      hasValidCoords = true;
    }
  });

  return hasValidCoords ? bounds : null;
};

/**
 * Convert HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
export const hslToHex = (h, s, l) => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Animation configuration constants
 */
export const ANIMATION_CONFIG = {
  NUM_SEGMENTS: 10,        // Reduced from 20 for better performance
  ANIMATION_INTERVAL: 60,  // Increased from 40ms for better performance
  STROKE_WEIGHT: 6,
  STROKE_OPACITY: 0.9,
  MAX_ZOOM_LEVEL: 13
};
